import {agendaDirectory} from "../access/patientDirectory";
import {patientDirectory} from "../access/patientDirectory";
import {
    collection,
    onSnapshot
} from "../access/firestore";
import {
    useEffect,
    useState,
    useRef
} from "react";
import { db } from "../firebase";
import { useClinic } from "../context/ClinicContext";
import {
    getDateKeyInTimeZone,
    getDeviceTimeZone,
    localizeScheduleEntry
} from "../utils/timezone";
import {
    CalendarDays,
    MessageCircle,
    WalletCards,
    UsersRound,
    ArrowUpRight,
    Sparkles,
    Activity,
    BarChart3,
    CalendarRange,
    TrendingUp
} from "lucide-react";

export default function
MobileDashboard({
    setSection,
    permissions = {}
}) {
    const { clinicId } = useClinic();
    const deviceTimeZone = getDeviceTimeZone();
    const canReadAppointments = Boolean(permissions.citas);
    const canReadPayments = Boolean(permissions.finanzas);
    const canReadPatients = Boolean(permissions.pacientes);
    const canReadWhatsApp = Boolean(permissions.whatsapp);

    const [citas, setCitas] =
        useState([]);

    const [pacientes, setPacientes] =
        useState([]);

    const [cuentasPorCobrar, setCuentasPorCobrar] =
        useState([]);

    const [conversations, setConversations] =
        useState([]);

    // =========================
    // WIDGET
    // =========================

    const [widgetIndex, setWidgetIndex] =
        useState(0);

    const widgetTouchStartX =
        useRef(null);

    const widgetTouchEndX =
        useRef(null);

    const [widgetAnimation, setWidgetAnimation] =
        useState("center");

    // =========================
    // CITAS
    // =========================

    useEffect(() => {

        setCitas([]);
        if (!clinicId || !canReadAppointments) return;

        return onSnapshot(

            agendaDirectory(db,clinicId),

            snapshot => {

                setCitas(

                        snapshot.docs.map(
                        doc => localizeScheduleEntry({
                            id: doc.id,
                            ...doc.data()
                        }, deviceTimeZone)
                    )

                );

            }

        );

    }, [clinicId, canReadAppointments, deviceTimeZone]);

    // =========================
    // PACIENTES
    // =========================

    useEffect(() => {

        setPacientes([]);
        if (!clinicId || !canReadPatients) return;

        return onSnapshot(

            patientDirectory(db,clinicId),

            snapshot => {

                setPacientes(

                    snapshot.docs.map(
                        doc => ({
                            id: doc.id,
                            ...doc.data()
                        })
                    )

                );

            }

        );

    }, [clinicId, canReadPatients]);

    // =========================
    // CUENTAS POR COBRAR
    // =========================

    useEffect(() => {

        if (
            !clinicId ||
            !canReadPayments ||
            pacientes.length === 0
        ) {

            setCuentasPorCobrar([]);

            return;

        }

        const cuentasActuales = {};
        setCuentasPorCobrar([]);

        const unsubscribes =
            pacientes.map(
                paciente => {

                    return onSnapshot(

                        collection(
                            db,
                            "clinics",
                            clinicId,
                            "pacientes",
                            paciente.id,
                            "cuentasPorCobrar"
                        ),

                        snapshot => {

                            cuentasActuales[
                                paciente.id
                            ] = snapshot.docs.map(
                                doc => ({
                                    id: doc.id,
                                    ...doc.data()
                                })
                            );

                            const todasLasCuentas =
                                Object.values(
                                    cuentasActuales
                                ).flat();

                            setCuentasPorCobrar(
                                todasLasCuentas
                            );

                        },

                        () => {

                            cuentasActuales[
                                paciente.id
                            ] = [];

                            const todasLasCuentas =
                                Object.values(
                                    cuentasActuales
                                ).flat();

                            setCuentasPorCobrar(
                                todasLasCuentas
                            );

                        }

                    );

                }
            );

        return () => {

            unsubscribes.forEach(
                unsubscribe =>
                    unsubscribe()
            );

        };

    }, [
        clinicId,
        canReadPayments,
        pacientes
    ]);

    // =========================
    // WHATSAPP
    // =========================

    useEffect(() => {

        setConversations([]);
        if (!clinicId || !canReadWhatsApp) return;

        return onSnapshot(

            collection(
                db,
                "clinics",
                clinicId,
                "whatsappConversations"
            ),

            snapshot => {

                setConversations(

                    snapshot.docs.map(
                        doc => ({
                            id: doc.id,
                            ...doc.data()
                        })
                    )

                );

            }

        );

    }, [clinicId, canReadWhatsApp]);

    // =========================
    // FECHA ACTUAL
    // =========================

    const fechaActual =
        new Date();

    const hoy =
        getDateKeyInTimeZone(
            fechaActual,
            deviceTimeZone
        );

    const mesActual =
        fechaActual.getMonth();

    const anioActual =
        fechaActual.getFullYear();

    // =========================
    // CITAS DE HOY
    // =========================

    const citasHoy =
        citas

            .filter(
                cita =>
                    cita.fecha === hoy
            )

            .sort(
                (a, b) =>
                    (a.hora || "")
                        .localeCompare(
                            b.hora || ""
                        )
            );

    // =========================
    // CITAS DEL MES
    // =========================

    const citasMes =
        citas.filter(
            cita => {

                if (!cita.fecha) {
                    return false;
                }

                const partes =
                    cita.fecha.split("-");

                if (partes.length !== 3) {
                    return false;
                }

                return (
                    Number(partes[0]) ===
                        anioActual
                    &&
                    Number(partes[1]) - 1 ===
                        mesActual
                );

            }
        );

    // =========================
    // CITAS DE ESTA SEMANA
    // =========================

    const inicioSemana =
        new Date(fechaActual);

    const diaSemana =
        inicioSemana.getDay();

    const diferenciaLunes =
        diaSemana === 0
            ? 6
            : diaSemana - 1;

    inicioSemana.setDate(
        inicioSemana.getDate() -
        diferenciaLunes
    );

    inicioSemana.setHours(
        0,
        0,
        0,
        0
    );

    const finSemana =
        new Date(
            inicioSemana
        );

    finSemana.setDate(
        finSemana.getDate() + 6
    );

    finSemana.setHours(
        23,
        59,
        59,
        999
    );

    const citasSemana =
        citas.filter(
            cita => {

                if (!cita.fecha) {
                    return false;
                }

                const partes =
                    cita.fecha.split("-");

                if (partes.length !== 3) {
                    return false;
                }

                const fechaCita =
                    new Date(
                        Number(partes[0]),
                        Number(partes[1]) - 1,
                        Number(partes[2])
                    );

                return (
                    fechaCita >= inicioSemana
                    &&
                    fechaCita <= finSemana
                );

            }
        );

    // =========================
    // WHATSAPP SIN LEER
    // =========================

    const whatsappUnread =
        conversations.reduce(

            (
                total,
                conversation
            ) =>

                total +
                Number(
                    conversation.unreadCount || 0
                ),

            0

        );

    // =========================
    // SALDO PENDIENTE
    // =========================

    const saldoPendiente =
        cuentasPorCobrar.reduce(

            (
                total,
                cuenta
            ) =>

                total +
                Number(
                    cuenta.saldo || 0
                ),

            0

        );

    // =========================
    // PACIENTES NUEVOS
    // =========================

    const pacientesMes =
        pacientes.filter(
            paciente => {

                if (
                    !paciente.createdAt?.seconds
                ) {
                    return false;
                }

                const fecha =
                    new Date(
                        paciente.createdAt.seconds *
                        1000
                    );

                return (
                    fecha.getMonth() ===
                        mesActual
                    &&
                    fecha.getFullYear() ===
                        anioActual
                );

            }
        ).length;

    // =========================
    // FORMATO MONEDA
    // =========================

    const formatearMoneda =
        valor =>
            `$${Number(
                valor || 0
            ).toLocaleString(
                "es-MX",
                {
                    minimumFractionDigits:
                        2,
                    maximumFractionDigits:
                        2
                }
            )}`;

    // =========================
    // RESÚMENES
    // =========================

    const resumenAgenda =

        citasHoy.length === 0

            ? "Sin citas programadas"

            : `${citasHoy.length} ${
                citasHoy.length === 1
                    ? "cita"
                    : "citas"
              } hoy`;

    const resumenWhatsApp =

        whatsappUnread === 0

            ? "Sin mensajes pendientes"

            : `${whatsappUnread} ${
                whatsappUnread === 1
                    ? "mensaje"
                    : "mensajes"
              } pendientes`;

    const resumenFinanzas =

        saldoPendiente > 0

            ? formatearMoneda(
                saldoPendiente
              )

            : "Sin saldo pendiente";

    const resumenPacientes =

        pacientesMes === 0

            ? "Sin nuevos registros"

            : `${pacientesMes} ${
                pacientesMes === 1
                    ? "nuevo registro"
                    : "nuevos registros"
              }`;

    // =========================
    // WIDGET HORIZONTAL
    // =========================

    const cambiarWidget =
        (direccion) => {

            if (
                widgetAnimation !==
                "center"
            ) {
                return;
            }

            setWidgetAnimation(
                direccion > 0
                    ? "exitLeft"
                    : "exitRight"
            );

            setTimeout(() => {

                setWidgetIndex(
                    actual => {

                        const siguiente =
                            actual + direccion;

                        if (siguiente < 0) {
                            return 4;
                        }

                        if (siguiente > 4) {
                            return 0;
                        }

                        return siguiente;

                    }
                );

                setWidgetAnimation(
                    direccion > 0
                        ? "enterRight"
                        : "enterLeft"
                );

                setTimeout(() => {

                    setWidgetAnimation(
                        "center"
                    );

                }, 45);

            }, 230);

        };

    const manejarTouchStart =
        event => {

            widgetTouchStartX.current =
                event.touches[0].clientX;

            widgetTouchEndX.current =
                null;

        };

    const manejarTouchMove =
        event => {

            widgetTouchEndX.current =
                event.touches[0].clientX;

        };

    const manejarTouchEnd =
        () => {

            if (
                widgetTouchStartX.current === null ||
                widgetTouchEndX.current === null
            ) {
                return;
            }

            const diferencia =
                widgetTouchStartX.current -
                widgetTouchEndX.current;

            if (
                Math.abs(diferencia) >= 35
            ) {

                if (diferencia > 0) {

                    cambiarWidget(1);

                } else {

                    cambiarWidget(-1);

                }

            }

            widgetTouchStartX.current =
                null;

            widgetTouchEndX.current =
                null;

        };

    // =========================
    // TARJETA
    // =========================

    const actionCard = (
        icon,
        title,
        description,
        accent
    ) => ({
        background:
            "#ffffff",
        border:
            "1px solid #e8edf3",
        borderRadius:
            22,
        padding:
            "18px",
        display:
            "flex",
        alignItems:
            "center",
        gap:
            14,
        boxShadow:
            "0 5px 18px rgba(15,23,42,0.045)",
        position:
            "relative",
        overflow:
            "hidden",
        boxSizing:
            "border-box"
    });

    // =========================
    // CONTENIDO DEL WIDGET
    // =========================

    const widgetContent = () => {

        if (widgetIndex === 0) {

            return {

                icon:
                    Activity,

                title:
                    "Actividad del consultorio",

                subtitle:
                    "Lo que está ocurriendo ahora.",

                value:
                    `${citasHoy.length} ${
                        citasHoy.length === 1
                            ? "cita"
                            : "citas"
                    } hoy`,

                detail:
                    whatsappUnread > 0
                        ? `${whatsappUnread} ${
                            whatsappUnread === 1
                                ? "mensaje"
                                : "mensajes"
                          } pendientes en WhatsApp`
                        : "No tienes mensajes pendientes",

                action:
                    "Ver actividad",

                actionSection:
                    whatsappUnread > 0
                        ? "whatsapp"
                        : "citas"

            };

        }

        if (widgetIndex === 1) {

            return {

                icon:
                    BarChart3,

                title:
                    "Resumen del mes",

                subtitle:
                    "Así se mueve tu consultorio.",

                value:
                    `${pacientesMes} ${
                        pacientesMes === 1
                            ? "paciente nuevo"
                            : "pacientes nuevos"
                    }`,

                detail:
                    `${citasMes.length} ${
                        citasMes.length === 1
                            ? "cita"
                            : "citas"
                    } registradas este mes`,

                action:
                    "Ver pacientes",

                actionSection:
                    "pacientes"

            };

        }

        if (widgetIndex === 2) {

            return {

                icon:
                    CalendarRange,

                title:
                    "Esta semana",

                subtitle:
                    "Tu agenda de los próximos días.",

                value:
                    `${citasSemana.length} ${
                        citasSemana.length === 1
                            ? "cita"
                            : "citas"
                    } programadas`,

                detail:
                    citasHoy.length > 0
                        ? `${citasHoy.length} ${
                            citasHoy.length === 1
                                ? "cita"
                                : "citas"
                          } corresponden a hoy`
                        : "Hoy no tienes citas programadas",

                action:
                    "Ver agenda",

                actionSection:
                    "citas"

            };

        }

        if (widgetIndex === 3) {

            return {

                icon:
                    TrendingUp,

                title:
                    "Rendimiento",

                subtitle:
                    "Indicadores generales.",

                value:
                    `${pacientes.length} ${
                        pacientes.length === 1
                            ? "paciente"
                            : "pacientes"
                    } registrados`,

                detail:
                    `${citasMes.length} ${
                        citasMes.length === 1
                            ? "cita"
                            : "citas"
                    } durante el mes`,

                action:
                    "Ver pacientes",

                actionSection:
                    "pacientes"

            };

        }

        return {

            icon:
                WalletCards,

            title:
                "Centro financiero",

            subtitle:
                "La situación pendiente de tu consultorio.",

            value:
                saldoPendiente > 0
                    ? formatearMoneda(
                        saldoPendiente
                      )
                    : "$0.00",

            detail:
                saldoPendiente > 0
                    ? "Saldo pendiente por cobrar"
                    : "No tienes saldo pendiente",

            action:
                "Ver finanzas",

            actionSection:
                "finanzas"

        };

    };

    const widget =
        widgetContent();

    const WidgetIcon =
        widget.icon;

    return (

        <div
            style={{
                minHeight:
                    "100vh",

                padding:
                    "18px 16px 110px",

                background:
                    "linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%)",

                boxSizing:
                    "border-box"
            }}
        >

            {/* HERO */}

            <div
                style={{
                    position:
                        "relative",

                    overflow:
                        "hidden",

                    borderRadius:
                        28,

                    padding:
                        "24px 22px 26px",

                    marginBottom:
                        18,

                    background:
                        "linear-gradient(135deg, #2563eb 0%, #1d4ed8 55%, #1e40af 100%)",

                    boxShadow:
                        "0 12px 30px rgba(37,99,235,0.22)"
                }}
            >

                <div
                    style={{
                        position:
                            "absolute",

                        width:
                            180,

                        height:
                            180,

                        borderRadius:
                            "50%",

                        background:
                            "rgba(255,255,255,0.08)",

                        top:
                            -90,

                        right:
                            -55
                    }}
                />

                <div
                    style={{
                        position:
                            "absolute",

                        width:
                            110,

                        height:
                            110,

                        borderRadius:
                            "50%",

                        background:
                            "rgba(255,255,255,0.06)",

                        bottom:
                            -65,

                        left:
                            -35
                    }}
                />

                <div
                    style={{
                        position:
                            "relative",

                        zIndex:
                            1
                    }}
                >

                    <div
                        style={{
                            display:
                                "flex",

                            alignItems:
                                "center",

                            gap:
                                8,

                            color:
                                "rgba(255,255,255,0.78)",

                            fontSize:
                                12,

                            fontWeight:
                                650,

                            marginBottom:
                                10
                        }}
                    >

                        <Sparkles
                            size={15}
                            strokeWidth={2}
                        />

                        TU JORNADA EN VEXADENT

                    </div>

                    <div
                        style={{
                            color:
                                "#ffffff",

                            fontSize:
                                27,

                            fontWeight:
                                780,

                            letterSpacing:
                                "-0.7px",

                            lineHeight:
                                1.12
                        }}
                    >

                        Todo tu consultorio,

                        <br />

                        en un solo lugar.

                    </div>

                    <div
                        style={{
                            color:
                                "rgba(255,255,255,0.78)",

                            fontSize:
                                13,

                            lineHeight:
                                1.5,

                            marginTop:
                                10,

                            maxWidth:
                                290
                        }}
                    >

                        Consulta rápidamente lo que requiere tu atención durante el día.

                    </div>

                </div>

            </div>

            {/* SECCIÓN */}

            <div
                style={{
                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "space-between",

                    marginBottom:
                        11,

                    padding:
                        "0 2px"
                }}
            >

                <div
                    style={{
                        fontSize:
                            17,

                        fontWeight:
                            750,

                        color:
                            "#0f172a",

                        letterSpacing:
                            "-0.2px"
                    }}
                >

                    Resumen de hoy

                </div>

                <div
                    style={{
                        fontSize:
                            12,

                        color:
                            "#64748b",

                        fontWeight:
                            600
                    }}
                >

                    VexaDent

                </div>

            </div>

            {/* GRID PRINCIPAL */}

            <div
                style={{
                    display:
                        "grid",

                    gridTemplateColumns:
                        "1fr 1fr",

                    gap:
                        10,

                    marginBottom:
                        18
                }}
            >

                {/* AGENDA */}

                <div
                    onClick={() =>
                        setSection("citas")
                    }

                    style={{
                        ...actionCard(),

                        cursor:
                            "pointer"
                    }}
                >

                    <CalendarDays
                        size={23}
                        color="#2563eb"
                        strokeWidth={2}
                    />

                    <div
                        style={{
                            minWidth:
                                0
                        }}
                    >

                        <div
                            style={{
                                fontSize:
                                    13,

                                fontWeight:
                                    750,

                                color:
                                    "#0f172a"
                            }}
                        >

                            Agenda

                        </div>

                        <div
                            style={{
                                fontSize:
                                    11,

                                color:
                                    "#64748b",

                                marginTop:
                                    4
                            }}
                        >

                            {resumenAgenda}

                        </div>

                    </div>

                </div>

                {/* WHATSAPP */}

                <div
                    onClick={() =>
                        setSection("whatsapp")
                    }

                    style={{
                        ...actionCard(),

                        cursor:
                            "pointer"
                    }}
                >

                    <MessageCircle
                        size={23}
                        color="#16a34a"
                        strokeWidth={2}
                    />

                    <div
                        style={{
                            minWidth:
                                0
                        }}
                    >

                        <div
                            style={{
                                fontSize:
                                    13,

                                fontWeight:
                                    750,

                                color:
                                    "#0f172a"
                            }}
                        >

                            WhatsApp

                        </div>

                        <div
                            style={{
                                fontSize:
                                    11,

                                color:
                                    "#64748b",

                                marginTop:
                                    4
                            }}
                        >

                            {resumenWhatsApp}

                        </div>

                    </div>

                </div>

                {/* FINANZAS */}

                <div
                    onClick={() =>
                        setSection("finanzas")
                    }

                    style={{
                        ...actionCard(),

                        cursor:
                            "pointer"
                    }}
                >

                    <WalletCards
                        size={23}
                        color="#f59e0b"
                        strokeWidth={2}
                    />

                    <div
                        style={{
                            minWidth:
                                0
                        }}
                    >

                        <div
                            style={{
                                fontSize:
                                    13,

                                fontWeight:
                                    750,

                                color:
                                    "#0f172a"
                            }}
                        >

                            Finanzas

                        </div>

                        <div
                            style={{
                                fontSize:
                                    11,

                                color:
                                    "#64748b",

                                marginTop:
                                    4
                            }}
                        >

                            {resumenFinanzas}

                        </div>

                    </div>

                </div>

                {/* PACIENTES */}

                <div
                    onClick={() =>
                        setSection("pacientes")
                    }

                    style={{
                        ...actionCard(),

                        cursor:
                            "pointer"
                    }}
                >

                    <UsersRound
                        size={23}
                        color="#7c3aed"
                        strokeWidth={2}
                    />

                    <div
                        style={{
                            minWidth:
                                0
                        }}
                    >

                        <div
                            style={{
                                fontSize:
                                    13,

                                fontWeight:
                                    750,

                                color:
                                    "#0f172a"
                            }}
                        >

                            Pacientes

                        </div>

                        <div
                            style={{
                                fontSize:
                                    11,

                                color:
                                    "#64748b",

                                marginTop:
                                    4
                            }}
                        >

                            {resumenPacientes}

                        </div>

                    </div>

                </div>

            </div>

            {/* WIDGET HORIZONTAL */}

            <div
                style={{
                    background:
                        "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",

                    border:
                        "1px solid #bfdbfe",

                    borderRadius:
                        26,

                    padding:
                        "18px",

                    boxShadow:
                        "0 12px 30px rgba(37,99,235,0.14), 0 3px 8px rgba(15,23,42,0.06)",

                    marginBottom:
                        18,

                    overflow:
                        "hidden",

                    position:
                        "relative"
                }}

                onTouchStart={
                    manejarTouchStart
                }

                onTouchMove={
                    manejarTouchMove
                }

                onTouchEnd={
                    manejarTouchEnd
                }
            >

                <div
                    style={{
                        display:
                            "flex",

                        alignItems:
                            "center",

                        justifyContent:
                            "space-between",

                        marginBottom:
                            14
                    }}
                >

                    <div
                        style={{
                            minWidth:
                                0
                        }}
                    >

                        <div
                            style={{
                                fontSize:
                                    17,

                                fontWeight:
                                    800,

                                color:
                                    "#0f172a",

                                letterSpacing:
                                    "-0.2px"
                            }}
                        >

                            Centro de información

                        </div>

                        <div
                            style={{
                                fontSize:
                                    12,

                                color:
                                    "#2563eb",

                                fontWeight:
                                    650,

                                marginTop:
                                    3
                            }}
                        >

                            Desliza para explorar

                        </div>

                    </div>

                    <div
                        style={{
                            display:
                                "flex",

                            alignItems:
                                "center",

                            gap:
                                4,

                            flexShrink:
                                0
                        }}
                    >

                        {[0, 1, 2, 3, 4].map(
                            index => (

                                <div
                                    key={index}
                                    style={{
                                        width:
                                            index === widgetIndex
                                                ? 22
                                                : 7,

                                        height:
                                            7,

                                        borderRadius:
                                            99,

                                        background:
                                            index === widgetIndex
                                                ? "#2563eb"
                                                : "#bfdbfe",

                                        transition:
                                            "all 0.25s ease"
                                    }}
                                />

                            )
                        )}

                    </div>

                </div>

                <div
                    style={{
                        position:
                            "relative",

                        borderRadius:
                            18,

                        background:
                            "linear-gradient(135deg, #2563eb 0%, #1d4ed8 58%, #1e40af 100%)",

                        border:
                            "1px solid rgba(255,255,255,0.18)",

                        padding:
                            "20px 18px",

                        minHeight:
                            166,

                        boxSizing:
                            "border-box",

                        userSelect:
                            "none",

                        touchAction:
                            "pan-x",

                        transform:
                            widgetAnimation === "exitLeft"
                                ? "translateX(-115%) scale(0.96)"
                                : widgetAnimation === "exitRight"
                                    ? "translateX(115%) scale(0.96)"
                                    : widgetAnimation === "enterRight"
                                        ? "translateX(115%) scale(0.96)"
                                        : widgetAnimation === "enterLeft"
                                            ? "translateX(-115%) scale(0.96)"
                                            : "translateX(0) scale(1)",

                        opacity:
                            widgetAnimation === "center"
                                ? 1
                                : 0,

                        transition:
                            "transform 230ms cubic-bezier(0.22, 1, 0.36, 1), opacity 230ms ease"
                    }}
                >

                    <div
                        style={{
                            display:
                                "flex",

                            alignItems:
                                "flex-start",

                            gap:
                                12
                        }}
                    >

                        <div
                            style={{
                                width:
                                    50,

                                height:
                                    50,

                                minWidth:
                                    50,

                                borderRadius:
                                    16,

                                background:
                                    "rgba(255,255,255,0.16)",

                                display:
                                    "flex",

                                alignItems:
                                    "center",

                                justifyContent:
                                    "center",

                                boxShadow:
                                    "0 6px 16px rgba(15,23,42,0.12)",

                                border:
                                    "1px solid rgba(255,255,255,0.18)"
                            }}
                        >

                            <WidgetIcon
                                size={24}
                                strokeWidth={2.2}
                                color="#ffffff"
                            />

                        </div>

                        <div
                            style={{
                                minWidth:
                                    0,

                                flex:
                                    1
                            }}
                        >

                            <div
                                style={{
                                    fontSize:
                                        15,

                                    fontWeight:
                                        800,

                                    color:
                                        "#ffffff"
                                }}
                            >

                                {widget.title}

                            </div>

                            <div
                                style={{
                                    fontSize:
                                        11,

                                    color:
                                        "rgba(255,255,255,0.76)",

                                    marginTop:
                                        3
                                }}
                            >

                                {widget.subtitle}

                            </div>

                        </div>

                        <button
                            type="button"
                            onClick={() =>
                                setSection(
                                    widget.actionSection
                                )
                            }
                            style={{
                                width:
                                    34,

                                height:
                                    34,

                                minWidth:
                                    34,

                                border:
                                    "none",

                                borderRadius:
                                    11,

                                background:
                                    "#ffffff",

                                boxShadow:
                                    "0 5px 14px rgba(15,23,42,0.14)",

                                display:
                                    "flex",

                                alignItems:
                                    "center",

                                justifyContent:
                                    "center",

                                padding:
                                    0,

                                cursor:
                                    "pointer"
                            }}
                        >

                            <ArrowUpRight
                                size={17}
                                strokeWidth={2}
                                color="#2563eb"
                            />

                        </button>

                    </div>

                    <div
                        style={{
                            marginTop:
                                18,

                            fontSize:
                                24,

                            fontWeight:
                                800,

                            color:
                                "#ffffff",

                            letterSpacing:
                                "-0.4px"
                        }}
                    >

                        {widget.value}

                    </div>

                    <div
                        style={{
                            marginTop:
                                5,

                            fontSize:
                                12,

                            color:
                                "rgba(255,255,255,0.78)"
                        }}
                    >

                        {widget.detail}

                    </div>

                </div>

                <div
                    style={{
                        display:
                            "flex",

                        justifyContent:
                            "center",

                        alignItems:
                            "center",

                        gap:
                            5,

                        marginTop:
                            12
                    }}
                >

                    <span
                        style={{
                            fontSize:
                                10,

                            color:
                                "#94a3b8"
                        }}
                    >

                        ←

                    </span>

                    <span
                        style={{
                            fontSize:
                                10,

                            color:
                                "#94a3b8"
                        }}
                    >

                        Desliza para descubrir más

                    </span>

                    <span
                        style={{
                            fontSize:
                                10,

                            color:
                                "#94a3b8"
                        }}
                    >

                        →

                    </span>

                </div>

            </div>

            {/* FRASE FINAL */}

            <div
                style={{
                    textAlign:
                        "center",

                    padding:
                        "8px 20px 20px",

                    color:
                        "#94a3b8",

                    fontSize:
                        11,

                    lineHeight:
                        1.5
                }}
            >

                VexaDent · Tu consultorio,

                <br />

                organizado en un solo lugar.

            </div>

        </div>

    );

}
